import React, { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { glyphs, Input, IDropdownOption, Icon, Typography } from "@treelab/panda";

import Select from "../../../Select";

import { IFilterValue, OperatorType, FilterCondition } from "../../../../../../types/form-type";
import { ITableColumn } from "../../../../../../types/table-type";

import { ICON_MAP } from "../../../../../../utils/form_secondary";
import { OPERATOR_OPTIONS } from "../../../../../../utils/filter_secondary";
import {
  getConditionOptions,
  FILTER_TYPE_MAP,
  convertFilterValueByCondition,
} from "../../../../../../utils/filter_main";

import { formatMessage } from "../../../../../../lib/intl";

import ValueInput from "../ValueInput";

import {
  Container,
  TopContainer,
  LeftContainer,
  RightContainer,
  MainContainer,
  Item,
  RemoveItem,
  Error,
} from "./styled";

interface IProps {
  index: number;
  column?: ITableColumn;
  columnOptions: ITableColumn[];
  value: IFilterValue;
  onRemove: (index: number) => void;
  operatorType: OperatorType;
  onOperatorChange: (value: OperatorType) => void;
  onValueChange: (value: IFilterValue, index: number) => void;
  shareUserId: string;
}

const FilterItem: React.FC<IProps> = ({
  value,
  onRemove,
  index,
  operatorType,
  columnOptions,
  column,
  onOperatorChange,
  onValueChange,
  shareUserId,
}) => {
  const [conditionOptions, setConditionOptions] = useState<IDropdownOption[]>([]);

  useEffect(() => {
    if (!column) return;
    const newConditionOptions = getConditionOptions(column);
    setConditionOptions(newConditionOptions);
  }, [column]);

  const getColOptions = useCallback(
    () =>
      columnOptions.map<IDropdownOption>((col) => ({
        name: col.name,
        value: col.id,
        // @ts-ignore
        icon: ICON_MAP[col.type],
      })),
    [columnOptions]
  );

  const handleDelete = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  const handleOperatorChange = useCallback(
    (value: OperatorType) => {
      onOperatorChange(value);
    },
    [onOperatorChange]
  );

  const handleColumnChange = useCallback(
    (data: any) => {
      const { columnId } = data as { columnId: string };
      const newColumn = columnOptions.find((item) => item.id === columnId);
      if (!newColumn) return;
      const newConditionOption = getConditionOptions(newColumn);
      const newValue = {
        ...value,
        columnId,
        filterType: FILTER_TYPE_MAP[newColumn.type],
        filterCondition: newConditionOption.length > 0 ? newConditionOption[0].value : FilterCondition.IS_EMPTY,
        filterString: null,
      };
      onValueChange(newValue as IFilterValue, index);
    },
    [columnOptions, index, onValueChange, value]
  );

  const handleConditionChange = useCallback(
    (data: any) => {
      const newValue = convertFilterValueByCondition(value, data.filterCondition);
      onValueChange(newValue, index);
    },
    [onValueChange, index, value]
  );

  const handleValueChange = useCallback(
    (data: any) => {
      const newValue = {
        ...value,
        ...data,
      };
      onValueChange(newValue, index);
    },
    [onValueChange, index, value]
  );

  return (
    <Container>
      <TopContainer type={index > 0 ? (index > 1 ? "text" : "select") : "none"}>
        {index > 1 && <FormattedMessage id={OPERATOR_OPTIONS.find((item) => item.value === operatorType)?.name} />}
        {index === 1 && (
          <Input.Select
            options={OPERATOR_OPTIONS.map(({ name: name_, value: value_ }) => ({
              value: value_,
              name: formatMessage({ id: name_ }),
            }))}
            input={{
              name: "",
              value: operatorType,
              onChange: handleOperatorChange,
            }}
            meta={{}}
          />
        )}
      </TopContainer>
      <MainContainer>
        <LeftContainer>
          <RemoveItem onClick={handleDelete}>
            <Icon icon={glyphs.REMOVE_CIRCLE_V2} />
          </RemoveItem>
        </LeftContainer>
        <RightContainer>
          <Item type="column">
            <Select
              options={getColOptions()}
              id="columnId"
              value={value.columnId}
              onChange={handleColumnChange}
              includeSearch
              usePinyinSearch
            />
          </Item>
          <Item type="condition">
            <Select
              options={conditionOptions}
              id="filterCondition"
              value={value.filterCondition}
              onChange={handleConditionChange}
            />
          </Item>
          <Item type="value">
            <ValueInput value={value} column={column} onChange={handleValueChange} shareUserId={shareUserId} />
          </Item>
        </RightContainer>
      </MainContainer>
      {(!column || FILTER_TYPE_MAP[column.type] !== value.filterType) && (
        <Error>
          <Typography type="caption">{formatMessage({ id: "filter.invalid" })}</Typography>
        </Error>
      )}
    </Container>
  );
};

export default FilterItem;
